import React, { useEffect } from 'react';

const ASI1VoiceDocumentation = () => {
  // Ensure scroll position is at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="container mx-auto sm:px-6 md:px-4 sm:py-8 max-w-7xl" style={{ paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-12 text-white">
          <h1 className="text-4xl font-bold mb-4">ASI-1 Voice Agent API Integration</h1>
          <p className="text-xl opacity-90">
            A React-based demo application that demonstrates integration with the ASI-1 Voice Agent API for conversational voice interactions.
          </p>
        </div>

        {/* Documentation content */}
        <div className="px-6 md:px-12 lg:px-16 py-8">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              This application provides a user interface for interacting with an AI conversational assistant through voice. 
              Users can speak to the AI using their device's microphone, and the AI will respond with synthesized speech. 
              The conversation maintains context through a session-based approach.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Video</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              This video provides a comprehensive demonstration of the ASI-1 Voice Agent API integration, showing all features in action.
            </p>
            <div style={{ position: "relative", paddingBottom: "59.53693495038589%", height: 0 }}>
              <iframe 
                src="https://www.loom.com/embed/35057d852f2a480b86570a911ea3e51a?sid=e1983c00-9142-4d20-acfd-613c2e780891" 
                frameBorder="0" 
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
                allowFullScreen 
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Installation</h2>
            
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Prerequisites</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Node.js (v14 or higher)</li>
                <li>npm (v6 or higher)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Setup</h3>
              <p className="text-gray-700 mb-2">Install dependencies</p>
              <div className="bg-gray-900 rounded-md p-4 mb-4">
                <code className="text-green-400 font-mono">npm install</code>
              </div>
              
              <p className="text-gray-700 mb-2">Start the development server</p>
              <div className="bg-gray-900 rounded-md p-4 mb-4">
                <code className="text-green-400 font-mono">npm start</code>
              </div>
              
              <p className="text-gray-700">The application will be available at <span className="font-mono bg-gray-100 p-1 rounded text-blue-600">http://localhost:3000</span></p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Features</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Real-time voice recording and playback
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Server-Sent Events (SSE) for streaming AI responses
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Visual status indicators
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Audio visualization during recording and playback
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Error handling with informative messages
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Session-based conversation (maintains context)
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Automatic retry mechanism for failed API calls
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Architecture</h2>
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Component Structure</h3>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <div className="ml-4 mb-2">
                  <div className="font-mono text-blue-800 mb-1">App.js: <span className="text-gray-700 font-normal">Main container component</span></div>
                  <div className="font-mono text-blue-800 mb-1">VoiceAgent.js: <span className="text-gray-700 font-normal">Core component managing voice interactions</span></div>
                  <div className="font-mono text-blue-800 mb-1">StatusIndicator.js: <span className="text-gray-700 font-normal">Visual indicator of current conversation state</span></div>
                  <div className="font-mono text-blue-800">VoiceVisualizer.js: <span className="text-gray-700 font-normal">Audio visualization for recording and playback</span></div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Utility Services</h3>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <div className="ml-4">
                  <div className="font-mono text-blue-800 mb-1">audioProcessor.js: <span className="text-gray-700 font-normal">Handles audio recording, processing, and playback</span></div>
                  <div className="font-mono text-blue-800 mb-1">sseHandler.js: <span className="text-gray-700 font-normal">Processes Server-Sent Events from the API</span></div>
                  <div className="font-mono text-blue-800">config.js: <span className="text-gray-700 font-normal">Configuration settings for API integration</span></div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">API Integration</h2>
            <p className="text-gray-700 mb-6">
              The application integrates with the ASI-1 Voice Agent API using the following approach:
            </p>

            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Configuration</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>API endpoint: <span className="font-mono bg-gray-100 p-1 rounded">https://voice-api.us.inc/api/process-voice-incremental</span></li>
                <li>Authentication: API key-based (<span className="font-mono bg-gray-100 p-1 rounded">&lt;API-KEY&gt;</span>)</li>
                <li>Content type: <span className="font-mono bg-gray-100 p-1 rounded">audio/wav</span></li>
                <li>Request timeout: 30 seconds</li>
                <li>Retry mechanism: 3 attempts with 1-second delay between retries</li>
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Proxy Setup</h3>
              <p className="text-gray-700 mb-2">
                The application uses http-proxy-middleware to handle CORS and other cross-domain issues when communicating with the API server.
              </p>
              <div className="bg-gray-900 rounded-md p-4">
                <pre className="text-green-400 font-mono overflow-x-auto">
{`// setupProxy.js
const { createProxyMiddleware } = require('http-proxy-middleware');

module.exports = function(app) {
  app.use(
    '/api',
    createProxyMiddleware({
      target: 'https://voice-api.us.inc',
      changeOrigin: true,
      pathRewrite: {
        '^/api': '/api'
      }
    })
  );
};`}
                </pre>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">API Communication Flow</h3>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                <li><strong>Connection Test:</strong> The app tests the API connection on startup</li>
                <li><strong>Audio Recording:</strong> User's voice is captured as WAV audio</li>
                <li><strong>API Request:</strong> Audio is sent to the API with session ID for context preservation</li>
                <li>
                  <strong>Streaming Response:</strong> The API responds with Server-Sent Events containing:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Status updates (recording, transcribing, thinking, speaking)</li>
                    <li>Audio chunks for AI response</li>
                    <li>Completion signals</li>
                    <li>Error messages</li>
                  </ul>
                </li>
              </ol>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Server-Sent Events Handling</h2>
            <p className="text-gray-700 mb-6">
              The application processes different event types from the API:
            </p>
            
            <div className="bg-gray-100 rounded-lg p-6">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Event Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-blue-700">status</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Updates the UI with the current processing status</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-blue-700">audio_chunk</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Queues audio chunks for sequential playback</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-blue-700">complete</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Signals the end of the AI response</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-mono text-blue-700">error</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Provides error information for handling</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">User Flow</h2>
            <div className="border-l-4 border-blue-500 pl-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Initial Connection</h3>
                <p className="text-gray-700">On load, the app tests connection to the voice API</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Start Recording</h3>
                <p className="text-gray-700">User clicks "Start Recording" to begin speaking</p>
                <ul className="list-disc pl-6 text-gray-600 mt-1">
                  <li>Status changes to "Listening..." (red pulsing indicator)</li>
                  <li>Voice visualizer activates</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Stop Recording</h3>
                <p className="text-gray-700">User clicks "Stop Recording" when done speaking</p>
                <ul className="list-disc pl-6 text-gray-600 mt-1">
                  <li>Status changes to "Processing..." (blue indicator)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Transcription</h3>
                <p className="text-gray-700">API initializes the audio</p>
                <ul className="list-disc pl-6 text-gray-600 mt-1">
                  <li>Status changes to "Transcribing..." (orange indicator)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">AI Processing</h3>
                <p className="text-gray-700">API generates a response</p>
                <ul className="list-disc pl-6 text-gray-600 mt-1">
                  <li>Status changes to "Thinking..." (blue indicator)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Response Playback</h3>
                <p className="text-gray-700">AI response audio plays</p>
                <ul className="list-disc pl-6 text-gray-600 mt-1">
                  <li>Status changes to "Speaking..." (green pulsing indicator)</li>
                  <li>User can click "Stop Response" to end playback</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Completion</h3>
                <p className="text-gray-700">System returns to idle state</p>
                <ul className="list-disc pl-6 text-gray-600 mt-1">
                  <li>Status changes to "Ready" (gray indicator)</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Status Indicators</h2>
            <p className="text-gray-700 mb-6">
              The application uses visual indicators to communicate the current state:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-2 bg-gray-100 rounded-lg flex items-center">
                <div className="h-4 w-4 bg-gray-400 rounded-full mr-3"></div>
                <span className="text-gray-700">Ready (idle/default)</span>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg flex items-center">
                <div className="h-4 w-4 bg-red-500 rounded-full mr-3 animate-pulse"></div>
                <span className="text-gray-700">Listening... (recording)</span>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg flex items-center">
                <div className="h-4 w-4 bg-orange-500 rounded-full mr-3"></div>
                <span className="text-gray-700">Transcribing...</span>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg flex items-center">
                <div className="h-4 w-4 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-gray-700">Processing... (thinking)</span>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg flex items-center">
                <div className="h-4 w-4 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                <span className="text-gray-700">Speaking...</span>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg flex items-center">
                <div className="h-4 w-4 bg-red-600 rounded-full mr-3"></div>
                <span className="text-gray-700">Error occurred</span>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error Handling</h2>
            <p className="text-gray-700 mb-6">
              The application handles various error scenarios:
            </p>
            <div className="bg-red-50 border border-red-100 rounded-lg p-6">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  Authentication failures (401)
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  CORS issues
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  Audio format problems
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  Size limit exceeded (10MB)
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  API rate limiting
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  Network connectivity issues
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  Timeout errors
                </li>
              </ul>
              <p className="mt-4 text-gray-700">Error messages are displayed to the user with appropriate recovery options.</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Development Notes</h2>
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Key Files</h3>
              <div className="bg-gray-100 p-6 rounded-lg">
                <ul className="text-gray-700 space-y-2">
                  <li className="font-mono text-blue-700">src/App.js: <span className="text-gray-700 font-normal">Main application container</span></li>
                  <li className="font-mono text-blue-700">src/components/VoiceAgent.js: <span className="text-gray-700 font-normal">Core voice interaction logic</span></li>
                  <li className="font-mono text-blue-700">src/utils/audioProcessor.js: <span className="text-gray-700 font-normal">Audio handling and API communication</span></li>
                  <li className="font-mono text-blue-700">src/utils/sseHandler.js: <span className="text-gray-700 font-normal">Server-Sent Events processing</span></li>
                  <li className="font-mono text-blue-700">src/config.js: <span className="text-gray-700 font-normal">API configuration</span></li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Development Commands</h3>
              <div className="bg-gray-900 rounded-md p-4 mb-2">
                <div className="text-green-400 font-mono">npm start: <span className="text-gray-300">Run development server</span></div>
              </div>
              <div className="bg-gray-900 rounded-md p-4 mb-2">
                <div className="text-green-400 font-mono">npm build: <span className="text-gray-300">Create production build</span></div>
              </div>
              <div className="bg-gray-900 rounded-md p-4 mb-2">
                <div className="text-green-400 font-mono">npm test: <span className="text-gray-300">Run tests</span></div>
              </div>
              <div className="bg-gray-900 rounded-md p-4">
                <div className="text-green-400 font-mono">npm eject: <span className="text-gray-300">Eject from create-react-app</span></div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Requirements</h2>
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Browser Requirements</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access to microphone (getUserMedia API)</li>
                <li>Support for MediaRecorder API</li>
                <li>Support for Server-Sent Events</li>
                <li>Modern JavaScript support</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Dependencies</h3>
              <div className="bg-gray-100 rounded-lg p-6">
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="font-mono text-blue-700 mr-4">React 18.2.0</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-mono text-blue-700 mr-4">http-proxy-middleware 3.0.3</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-mono text-blue-700 mr-4">react-scripts 5.0.1</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ASI1VoiceDocumentation;
